import { Icon as AntIcon, Input, Checkbox } from "antd";
import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SET_ALERT, SetAlertAction } from "../../actions/actionTypes";
import { submitVariationForPublishing } from "../../api/WebAPI";
import { Button } from "../Button";
import { CustomMixInfo, InitialTrackData, MainStore } from "../../store";
import { Container, FlexRow, FlexColumn } from "./styles";
import AntModal from "../../../components/AntModal";
import Spinner from "../../../components/Spinner";
import { lightInputStyle } from "../../../globalStyles";
import SignatureCanvas from "react-signature-canvas/dist/index.umd.min.js";

const { TextArea } = Input;

export const VariationModal = ({ modalState }) => {
  const VARIATION_SUBMIT_SUCCESS =
    "Your custom mix has been submitted as a variation and will be reviewed shortly. Thanks for contributing!";
  const VARIATION_SUBMIT_ERROR =
    "There was an error submitting your mix as a variation. Please try again or contact our team for assistance.";
  const VARIATION_INVALID_FORM = "Please fill out all required fields.";

  const dispatch = useDispatch();

  const customMixInfo = useSelector<MainStore, CustomMixInfo>(
    state => state.project.customMixInfo
  );
  const initialTrackData = useSelector<MainStore, InitialTrackData>(
    state => state.project.initialTrackData
  );

  // form fields
  const [variationTitle, setVariationTitle] = useState(null);
  const [
    containsOriginalUploadedContent,
    setContainsOriginalUploadedContent
  ] = useState(false);
  const [isCutdown, setIsCutdown] = useState(false);
  const [variationNotes, setVariationNotes] = useState(null);
  const [toolsToCreate, setToolsToCreate] = useState(null);
  const [noExternalTools, setNoExternalTools] = useState(false);
  const [iReadAndAgree, setIReadAndAgree] = useState(false);
  const [fullName, setFullName] = useState(null);

  const [isVariationSubmitting, setIsVariationSubmitting] = useState(false);
  // id -> error message
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const signatureCanvas = useRef(null);

  // helper to reset all form fields
  const resetVariationForm = () => {
    setVariationTitle(null);
    setContainsOriginalUploadedContent(false);
    setIsCutdown(false);
    setVariationNotes(null);
    setToolsToCreate(null);
    setNoExternalTools(false);
    setIReadAndAgree(false);
    setFullName(null);
    setFormErrors({});
    if (signatureCanvas.current && signatureCanvas.current.clear) {
      signatureCanvas.current.clear();
    }
  };

  const validateVariationForm = () => {
    const errors = {} as { [key: string]: string };
    if (!variationTitle) {
      errors["variationTitle"] = "Variation title is required";
    }
    if (!noExternalTools && !toolsToCreate) {
      errors["toolsToCreate"] =
        "Tools used to create the variation are required";
    }
    if (!iReadAndAgree) {
      errors["iReadAndAgree"] = "You must agree to the terms";
    }
    if (signatureCanvas.current.isEmpty()) {
      errors["signature"] = "Signature is required";
    }
    if (!fullName) {
      errors["fullName"] = "Your name is required";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmitVariation = async () => {
    if (!validateVariationForm()) {
      dispatch<SetAlertAction>({
        type: SET_ALERT,
        id: "SUBMIT_VARIATION",
        role: "error",
        title: "Error Submitting Variation",
        message: VARIATION_INVALID_FORM
      });
      return;
    }

    const e = signatureCanvas.current.getTrimmedCanvas();
    const signatureBase64 = e.toDataURL("image/png");

    setIsVariationSubmitting(true);
    await submitVariationForPublishing(
      customMixInfo,
      variationTitle,
      containsOriginalUploadedContent,
      isCutdown,
      variationNotes,
      toolsToCreate,
      signatureBase64,
      fullName
    )
      .then(res => {
        dispatch<SetAlertAction>({
          type: SET_ALERT,
          id: "SUBMIT_VARIATION",
          role: "success",
          title: "Variation Submitted",
          message: VARIATION_SUBMIT_SUCCESS
        });
        setIsVariationSubmitting(false);
        resetVariationForm();
        modalState.close();
      })
      .catch(() => {
        dispatch<SetAlertAction>({
          type: SET_ALERT,
          id: "SUBMIT_VARIATION",
          role: "error",
          title: "Error Submitting Variation",
          message: VARIATION_SUBMIT_ERROR
        });
        setIsVariationSubmitting(false);
      });
  };

  const onTitleChange = e => {
    const processedTitle = e.target.value
      .toLowerCase()
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    setVariationTitle(processedTitle);
    setFormErrors(prevErrors => {
      if (processedTitle) {
        const { variationTitle, ...rest } = prevErrors;
        return rest;
      }
      return prevErrors;
    });
  };

  const onFullNameChange = e => {
    const submitterName = e.target.value;
    setFullName(submitterName);
    setFormErrors(prevErrors => {
      if (submitterName !== "") {
        const { fullName, ...rest } = prevErrors;
        return rest;
      }
      return prevErrors;
    });
  };

  return (
    <AntModal modalState={modalState}>
      {initialTrackData.id ? (
        <Container id="track-info-container">
          <h2 className="title">Submit New Variation</h2>
          <div id="title-container">
            <div className="trackinfo-label">Title</div>
            {formErrors["variationTitle"] && (
              <div className="input-error">{formErrors["variationTitle"]}</div>
            )}
            <FlexRow>
              <div className="trackinfo-title">{initialTrackData.title} |</div>
              <FlexColumn className="variation-title-container">
                <Input
                  name="variation-title"
                  className="variation-title"
                  value={variationTitle}
                  style={{ ...lightInputStyle }}
                  onChange={onTitleChange}
                />
                <div className="caption">
                  <img
                    className="i-icon"
                    src={require("../../../assets/quicktip_info.svg")}
                    alt=""
                  />
                  Cutdowns (different length versions with few alterations)
                  should following our naming convention: Sting, 15 Sec, 30 Sec,
                  1 Min, 1:30 Min, etc.
                </div>
              </FlexColumn>
            </FlexRow>
          </div>
          <FlexRow>
            <div className="trackinfo-label">
              Does this track contain original (uploaded) content?
            </div>
            <Checkbox
              onChange={e =>
                setContainsOriginalUploadedContent(e.target.checked)
              }
            />
          </FlexRow>
          <div id="cutdown-container">
            <FlexRow>
              <div className="trackinfo-label">
                Is this variation a cutdown (alternate length version)?
              </div>
              <Checkbox onChange={e => setIsCutdown(e.target.checked)} />
            </FlexRow>
            <div className="caption">
              <img
                className="i-icon"
                src={require("../../../assets/quicktip_info.svg")}
                alt=""
              />
              A cutdown track doesn't have significant alterations apart from
              the length.
            </div>
          </div>
          <div id="variation-notes-container">
            <div className="trackinfo-label">
              Any notes or comments about the variation you created? Tell us
              here!
            </div>
            <TextArea
              name="variation-notes"
              className="variation-tools"
              rows={4}
              style={{ ...lightInputStyle }}
              value={variationNotes}
              onChange={e => {
                setVariationNotes(e.target.value);
              }}
            />
          </div>
          <div id="no-external-tools-container">
            <FlexRow>
              <div className="trackinfo-label">
                No external tools or libraries were used in this custom mix
              </div>
              <Checkbox
                name="no-external-tools"
                checked={noExternalTools}
                onChange={e => {
                  setNoExternalTools(e.target.checked);
                  if (e.target.checked) {
                    setFormErrors(prevErrors => {
                      const { toolsToCreate, ...rest } = prevErrors;
                      return rest;
                    });
                  }
                }}
              />
            </FlexRow>
          </div>
          {!noExternalTools && (
            <div id="tools-container">
              <div className="trackinfo-label">
                Please list in detail which tools were used to create this
                variation
              </div>
              {formErrors["toolsToCreate"] && (
                <div className="input-error">{formErrors["toolsToCreate"]}</div>
              )}
              <div className="caption">
                <img
                  className="i-icon"
                  src={require("../../../assets/quicktip_info.svg")}
                  alt=""
                />
                Include all names of software, sample libraries, equipment, and
                instruments used. All software and tools must be fully licensed
                for this purpose by you. No loops may be used in your
                composition.
              </div>
              <TextArea
                name="variation-tools"
                className="variation-tools"
                rows={4}
                style={{ ...lightInputStyle }}
                value={toolsToCreate}
                onChange={e => {
                  setToolsToCreate(e.target.value);
                  setFormErrors(prevErrors => {
                    const { toolsToCreate, ...rest } = prevErrors;
                    return rest;
                  });
                }}
              />
            </div>
          )}
          <div id="legal-agreement-container">
            <div className="trackinfo-label">
              Submission Agreement for Variation Editors
            </div>
            <div className="variation-legal-agreement">
              By submitting this variation, I affirm and agree to the following
              terms:
              <ol>
                <li>
                  I grant Blue Dot Sessions and its affiliates, successors, and
                  assigns the exclusive right to monetize, distribute,
                  reproduce, publicly perform, and otherwise exploit this
                  variation in any format, medium, or platform, worldwide and in
                  perpetuity. This includes but is not limited to licensing,
                  streaming, synchronization, and derivative works.
                </li>
                <li>
                  I confirm that this variation is an original creation derived
                  solely from the materials provided within the Blue Dot
                  Sessions system or is otherwise lawfully created by me. I
                  further affirm that this submission does not contain any
                  third-party material that is subject to copyright protection
                  or elements that infringe on the rights of any other person or
                  entity. If third-party material is used, I confirm that it is
                  in the public domain or otherwise free of copyright
                  restrictions.
                </li>
                <li>
                  I acknowledge that once submitted, this variation becomes part
                  of the Blue Dot Sessions library and may be modified, adapted,
                  or used in future works at the sole discretion of Blue Dot
                  Sessions without further compensation or approval required.
                </li>
                <li>
                  I understand that my submission is final and cannot be
                  revoked, rescinded, or withdrawn, and I waive any claims or
                  rights of ownership beyond those granted herein. However, if I
                  make revisions or modifications to a previously submitted
                  variation, I may submit the updated version for approval as a
                  new variation, subject to the same terms and review process.
                </li>
                <li>
                  I agree to indemnify and hold harmless Blue Dot Sessions and
                  its affiliates from any claims, liabilities, damages, or legal
                  expenses arising from any breach of this agreement, including
                  but not limited to copyright infringement or misrepresentation
                  of authorship.
                </li>
              </ol>
            </div>
          </div>
          <div id="agree-container">
            {formErrors["iReadAndAgree"] && (
              <div className="input-error">{formErrors["iReadAndAgree"]}</div>
            )}
            <FlexRow>
              <div className="trackinfo-label">
                I have read, understand, and agree to the above terms
              </div>
              <Checkbox
                name="i-read-and-agree"
                onChange={e => {
                  setIReadAndAgree(e.target.checked);
                  if (e.target.checked) {
                    setFormErrors(prevErrors => {
                      const { iReadAndAgree, ...rest } = prevErrors;
                      return rest;
                    });
                  }
                }}
              />
            </FlexRow>
          </div>
          <div id="signature-container">
            <div id="signature-canvas-header">
              <div className="sign-label">
                Sign here{" "}
                <img
                  className="pulsing-i-icon"
                  src={require("../../../assets/arrow-down.svg")}
                  alt=""
                />
              </div>
              <div
                id="signature-clear"
                onClick={() => signatureCanvas.current.clear()}
              >
                Clear
              </div>
            </div>
            {formErrors["signature"] && (
              <div className="input-error">{formErrors["signature"]}</div>
            )}
            <SignatureCanvas
              ref={signatureCanvas}
              penColor="#5D95AA"
              backgroundColor="rgb(54, 54, 54)"
              canvasProps={{
                width: 600,
                height: 100,
                className: "signature-canvas"
              }}
              onBegin={() =>
                setFormErrors(prevErrors => {
                  const { signature, ...rest } = prevErrors;
                  return rest;
                })
              }
            />
          </div>
          <div id="full-name-container">
            <div className="trackinfo-label">Full Name</div>
            {formErrors["fullName"] && (
              <div className="input-error">{formErrors["fullName"]}</div>
            )}
            <Input
              name="variation-submitter"
              className="variation-submitter"
              value={fullName}
              style={{ ...lightInputStyle }}
              onChange={onFullNameChange}
            />
          </div>
          <FlexRow>
            <Button height="auto" px="24" onClick={handleSubmitVariation}>
              {isVariationSubmitting && (
                <AntIcon style={{ marginRight: 10 }} type="loading" />
              )}
              Submit Variation
            </Button>
          </FlexRow>
        </Container>
      ) : (
        <Container id="track-info-container">
          <Spinner height="50px" />
        </Container>
      )}
    </AntModal>
  );
};
